import { forwardRef } from 'react';

import type { ObjectAsConst } from 'Types';

import { useLever } from './hooks';
import { ExtractLeverValueType, LeverKey } from './types';

type FilterKeys<T, PropValue> = { [K in keyof T]: T[K] extends PropValue ? K : never }[keyof T];

const connectConfigService = <
  TLevers extends Partial<{ [TLeverKey in LeverKey]: string }>,
  // maps mapLeversToProps objects like { 'profile.bumper_sticker_enabled': 'isStickerEnabled' }
  // to prop objects like { isStickerEnabled: boolean }
  TInjectedLeverProps = TLevers[keyof TLevers] extends string
    ? {
        [TLeverKey in TLevers[keyof TLevers]]: ExtractLeverValueType<
          FilterKeys<TLevers, TLeverKey> & LeverKey
        >;
      }
    : never
>(
  mapLeversToProps: ObjectAsConst<TLevers>
) => {
  const levers = Object.keys(mapLeversToProps).map((key) => [key, mapLeversToProps[key]]) as [
    LeverKey,
    string
  ][];
  const { length } = levers;

  return <TProps extends TInjectedLeverProps>(WrappedComponent: React.ComponentType<TProps>) =>
    forwardRef((props, ref) => {
      const leverProps = {};

      // It's safe to create hooks while iterating here because the length of levers is consistent
      for (let i = 0; i < length; i += 1) {
        const [key, propName] = levers[i];
        if (!__TEST__) {
          leverProps[propName] = useLever(key); // eslint-disable-line react-hooks/rules-of-hooks
        }
      }

      if (!WrappedComponent) {
        return null;
      }

      return <WrappedComponent ref={ref} {...(leverProps as any)} {...props} />;
    }) as unknown as React.ComponentType<Omit<TProps, keyof TInjectedLeverProps>>;
};

export default connectConfigService;
